import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    X, Calendar, Tag, Trash2, CheckSquare, Square, Plus, Search,
    Building2, User as UserIcon, MessageSquare, Paperclip, Activity, Archive, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';

const PRIORITY_META = {
    low:     { label: 'Low',    dot: '#94a3b8' },
    medium:  { label: 'Medium', dot: '#3b82f6' },
    high:    { label: 'High',   dot: '#f59e0b' },
    urgent:  { label: 'Urgent', dot: '#dc2626' },
};

// Debounce hook for title/description auto-save
function useDebouncedSave(value, save, delay = 800) {
    const initial = React.useRef(true);
    useEffect(() => {
        if (initial.current) { initial.current = false; return; }
        const t = setTimeout(() => { save(); }, delay);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
}

export default function CardModal({
    cardId, board, clients, users, canEdit, canDelete,
    onClose, onUpdated, onDeleted,
}) {
    const { user } = useAuth();
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(true);

    // Local editable copies (debounced -> server)
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/backlog/cards/${cardId}`);
            setCard(r.data);
            setTitle(r.data.title || '');
            setDescription(r.data.description || '');
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to load card');
            onClose();
        } finally {
            setLoading(false);
        }
    }, [cardId, onClose]);
    useEffect(() => { load(); }, [load]);

    // Helper: PATCH then update local + parent
    const patch = useCallback(async (body) => {
        try {
            await api.patch(`/backlog/cards/${cardId}`, body);
            setCard(c => c ? { ...c, ...body } : c);
            onUpdated(cardId, body);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Save failed');
        }
    }, [cardId, onUpdated]);

    // Auto-save title
    useDebouncedSave(title, () => {
        if (!card || title === card.title) return;
        if (!title.trim()) { setTitle(card.title); return; }
        patch({ title: title.trim() });
    });
    // Auto-save description
    useDebouncedSave(description, () => {
        if (!card || description === card.description) return;
        patch({ description });
    });

    const close = () => onClose();

    const handleDelete = async () => {
        if (!window.confirm('Delete this card permanently? This cannot be undone.')) return;
        try {
            await api.delete(`/backlog/cards/${cardId}`);
            onDeleted(cardId);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Delete failed');
        }
    };

    if (loading || !card) {
        return (
            <Dialog open onOpenChange={(o) => !o && close()}>
                <DialogContent className="max-w-3xl">
                    <DialogTitle className="sr-only">Loading card</DialogTitle>
                    <div className="py-12 text-center text-sm text-slate-500">Loading…</div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open onOpenChange={(o) => !o && close()}>
            <DialogContent className="max-w-3xl p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="card-modal">
                <DialogTitle className="sr-only">{card.title || 'Card details'}</DialogTitle>
                <DialogDescription className="sr-only">
                    Edit card details — title, description, labels, clients, assignees, due date, checklist, attachments and comments.
                </DialogDescription>
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200">
                    <div className="flex-1">
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="text-lg font-semibold border-none px-0 shadow-none focus-visible:ring-0 h-auto"
                            placeholder="Card title…"
                            disabled={!canEdit}
                            data-testid="card-title-input"
                        />
                        <div className="text-[11px] text-slate-400 mt-0.5">
                            Created {fmtTs(card.created_at)} · Updated {fmtTs(card.updated_at)}
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {canDelete && (
                            <button onClick={handleDelete} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Delete" data-testid="card-delete-btn">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                        <button onClick={close} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="card-close-btn">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto grid grid-cols-3 gap-0">
                    {/* Main column */}
                    <div className="col-span-2 px-5 py-4 space-y-5 border-r border-slate-200">
                        {/* Chips row: priority, due date, labels, clients, assignees */}
                        <ChipRow card={card} board={board} clients={clients} users={users} patch={patch} canEdit={canEdit} />

                        <Section title="Description" icon={null}>
                            <Textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Add a more detailed description… (auto-saves)"
                                rows={5}
                                className="resize-y"
                                disabled={!canEdit}
                                data-testid="card-description"
                            />
                        </Section>

                        <Section title="Checklist" icon={CheckSquare}>
                            <Checklist
                                card={card}
                                onChange={(checklist) => setCard(c => ({ ...c, checklist }))}
                                canEdit={canEdit}
                            />
                        </Section>

                        <Section title="Attachments" icon={Paperclip}>
                            <Attachments
                                card={card}
                                onChange={(attachments) => setCard(c => ({ ...c, attachments }))}
                                canEdit={canEdit}
                            />
                        </Section>

                        <Section title="Comments" icon={MessageSquare}>
                            <Comments
                                card={card}
                                onChange={(comments) => setCard(c => ({ ...c, comments }))}
                                currentUser={user}
                                canEdit={canEdit}
                            />
                        </Section>
                    </div>

                    {/* Side column: activity log */}
                    <div className="px-4 py-4 bg-slate-50/50">
                        <Section title="Activity" icon={Activity} compact>
                            <ActivityLog activity={card.activity || []} />
                        </Section>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


// ───────── Sub-components ─────────

function Section({ title, icon: Icon, compact, children }) {
    return (
        <div>
            <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold ${compact ? 'mb-1.5' : 'mb-2'}`}>
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {title}
            </div>
            {children}
        </div>
    );
}


function ChipRow({ card, board, clients, users, patch, canEdit }) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            <PriorityChip value={card.priority} onChange={(v) => patch({ priority: v })} canEdit={canEdit} />
            <DueDateChip value={card.due_date} onChange={(v) => patch({ due_date: v })} canEdit={canEdit} />
            <LabelsChip
                value={card.label_ids}
                labels={board.labels}
                onChange={(v) => patch({ label_ids: v })}
                canEdit={canEdit}
            />
            <ClientsChip
                value={card.client_ids}
                clients={clients}
                onChange={(v) => patch({ client_ids: v })}
                canEdit={canEdit}
            />
            <AssigneesChip
                value={card.assignee_ids}
                users={users}
                onChange={(v) => patch({ assignee_ids: v })}
                canEdit={canEdit}
            />
        </div>
    );
}


function PriorityChip({ value, onChange, canEdit }) {
    const meta = PRIORITY_META[value] || PRIORITY_META.medium;
    return (
        <Select value={value || 'medium'} onValueChange={onChange} disabled={!canEdit}>
            <SelectTrigger className="h-8 w-auto px-2.5 gap-2 text-[12.5px] border-slate-300" data-testid="card-priority">
                <span className="w-2 h-2 rounded-full" style={{ background: meta.dot }} />
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {Object.entries(PRIORITY_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>
                        <span className="inline-flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ background: m.dot }} />
                            {m.label}
                        </span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}


function DueDateChip({ value, onChange, canEdit }) {
    return (
        <div className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-slate-300 bg-white text-[12.5px]">
            <Calendar className="w-3.5 h-3.5 text-slate-500" />
            <input
                type="date"
                value={(value || '').slice(0, 10)}
                onChange={(e) => onChange(e.target.value || null)}
                disabled={!canEdit}
                className="bg-transparent text-slate-700 focus:outline-none"
                data-testid="card-due-date"
            />
            {value && canEdit && (
                <button onClick={() => onChange(null)} className="text-slate-400 hover:text-slate-700">
                    <X className="w-3 h-3" />
                </button>
            )}
        </div>
    );
}


function MultiSelectChip({ label, icon: Icon, items, value, onChange, canEdit, getLabel, getColor, testId }) {
    const [query, setQuery] = useState('');
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter(i => getLabel(i).toLowerCase().includes(q));
    }, [items, query, getLabel]);
    const selected = items.filter(i => value.includes(i.id));
    const toggle = (id) =>
        onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);

    return (
        <Popover onOpenChange={(o) => { if (!o) setQuery(''); }}>
            <PopoverTrigger asChild>
                <button
                    className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-slate-300 bg-white text-[12.5px] hover:border-slate-400"
                    disabled={!canEdit}
                    data-testid={testId}
                >
                    <Icon className="w-3.5 h-3.5 text-slate-500" />
                    {selected.length === 0 ? (
                        <span className="text-slate-500">{label}</span>
                    ) : (
                        <span className="flex items-center gap-1">
                            {selected.slice(0, 3).map(s => (
                                <span
                                    key={s.id}
                                    className="text-[10px] font-medium px-1 py-0.5 rounded text-white"
                                    style={{ background: getColor(s) }}
                                >
                                    {getLabel(s)}
                                </span>
                            ))}
                            {selected.length > 3 && <span className="text-[11px] text-slate-500">+{selected.length - 3}</span>}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-1 pb-1">{label}</div>
                {items.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-slate-400">No options</div>
                ) : (
                    <>
                        {items.length > 6 && (
                            <div className="relative mb-1">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                <Input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={`Search ${label.toLowerCase()}…`}
                                    className="h-7 text-[12.5px] pl-7"
                                    autoFocus
                                    data-testid={`${testId}-search`}
                                />
                            </div>
                        )}
                        <div className="max-h-64 overflow-y-auto">
                            {filtered.length === 0 ? (
                                <div className="px-2 py-3 text-xs text-slate-400">No matches</div>
                            ) : filtered.map(it => (
                                <button
                                    key={it.id}
                                    onClick={() => toggle(it.id)}
                                    className={`w-full text-left text-[13px] px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 ${
                                        value.includes(it.id) ? 'bg-slate-100 font-medium' : ''
                                    }`}
                                >
                                    <span className="w-3 h-3 rounded flex-shrink-0" style={{ background: getColor(it) }} />
                                    <span className="flex-1 truncate">{getLabel(it)}</span>
                                    {value.includes(it.id) && <span className="text-slate-900">✓</span>}
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </PopoverContent>
        </Popover>
    );
}


function LabelsChip({ value, labels, onChange, canEdit }) {
    return (
        <MultiSelectChip
            label="Labels"
            icon={Tag}
            items={labels}
            value={value || []}
            onChange={onChange}
            canEdit={canEdit}
            getLabel={(l) => l.name}
            getColor={(l) => l.color}
            testId="card-labels"
        />
    );
}


function ClientsChip({ value, clients, onChange, canEdit }) {
    return (
        <MultiSelectChip
            label="Clients"
            icon={Building2}
            items={clients}
            value={value || []}
            onChange={onChange}
            canEdit={canEdit}
            getLabel={(c) => c.name}
            getColor={() => '#475569'}
            testId="card-clients"
        />
    );
}


function AssigneesChip({ value, users, onChange, canEdit }) {
    return (
        <MultiSelectChip
            label="Assignees"
            icon={UserIcon}
            items={users}
            value={value || []}
            onChange={onChange}
            canEdit={canEdit}
            getLabel={(u) => u.name}
            getColor={() => '#1e293b'}
            testId="card-assignees"
        />
    );
}


// ───────── Checklist ─────────

function Checklist({ card, onChange, canEdit }) {
    const [newItem, setNewItem] = useState('');
    const items = card.checklist || [];
    const done = items.filter(i => i.done).length;
    const total = items.length;

    const add = async () => {
        const t = newItem.trim();
        if (!t) return;
        try {
            const r = await api.post(`/backlog/cards/${card.id}/checklist`, { label: t });
            onChange([...items, r.data.item]);
            setNewItem('');
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed');
        }
    };
    const toggle = async (it) => {
        const newDone = !it.done;
        onChange(items.map(x => x.id === it.id ? { ...x, done: newDone } : x));
        try {
            await api.patch(`/backlog/cards/${card.id}/checklist/${it.id}`, { done: newDone });
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed');
            onChange(items);  // revert
        }
    };
    const remove = async (it) => {
        const old = items;
        onChange(items.filter(x => x.id !== it.id));
        try {
            await api.delete(`/backlog/cards/${card.id}/checklist/${it.id}`);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed');
            onChange(old);
        }
    };

    return (
        <div>
            {total > 0 && (
                <div className="mb-2">
                    <div className="text-[11px] text-slate-500 mb-1">{done} / {total} done</div>
                    <div className="h-1 bg-slate-200 rounded overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: total ? `${(done / total) * 100}%` : '0%' }} />
                    </div>
                </div>
            )}
            <div className="space-y-1">
                {items.map(it => (
                    <div key={it.id} className="flex items-center gap-2 group">
                        <button onClick={() => canEdit && toggle(it)} disabled={!canEdit} className="text-slate-500 hover:text-slate-700">
                            {it.done ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4" />}
                        </button>
                        <span className={`flex-1 text-[13px] ${it.done ? 'line-through text-slate-400' : 'text-slate-800'}`}>{it.label}</span>
                        {canEdit && (
                            <button onClick={() => remove(it)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                ))}
            </div>
            {canEdit && (
                <div className="flex items-center gap-1.5 mt-2">
                    <Input
                        value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                        placeholder="Add a checklist item…"
                        className="h-8 text-sm"
                        onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                        data-testid="checklist-input"
                    />
                    <Button size="sm" className="h-8 px-2.5" onClick={add} data-testid="checklist-add">
                        <Plus className="w-3.5 h-3.5" />
                    </Button>
                </div>
            )}
        </div>
    );
}


// ───────── Attachments (URL only) ─────────

function Attachments({ card, onChange, canEdit }) {
    const [name, setName] = useState('');
    const [url, setUrl]   = useState('');
    const items = card.attachments || [];

    const add = async () => {
        const n = name.trim() || url.trim();
        const u = url.trim();
        if (!u) return;
        try {
            const r = await api.post(`/backlog/cards/${card.id}/attachments`, { name: n, url: u });
            onChange([...items, r.data.attachment]);
            setName(''); setUrl('');
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed');
        }
    };
    const remove = async (a) => {
        const old = items;
        onChange(items.filter(x => x.id !== a.id));
        try { await api.delete(`/backlog/cards/${card.id}/attachments/${a.id}`); }
        catch (e) { toast.error('Failed'); onChange(old); }
    };

    return (
        <div className="space-y-2">
            {items.length === 0 && <div className="text-xs text-slate-400 italic">No attachments</div>}
            {items.map(a => (
                <div key={a.id} className="flex items-center gap-2 p-2 rounded border border-slate-200 bg-white text-[12.5px]">
                    <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-slate-800 hover:underline inline-flex items-center gap-1">
                        {a.name} <ExternalLink className="w-3 h-3 text-slate-400" />
                    </a>
                    {canEdit && (
                        <button onClick={() => remove(a)} className="text-slate-400 hover:text-rose-600">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            ))}
            {canEdit && (
                <div className="grid grid-cols-[1fr,1.5fr,auto] gap-1.5">
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="h-8 text-sm" />
                    <Input value={url}  onChange={(e) => setUrl(e.target.value)}  placeholder="https://…"        className="h-8 text-sm"
                        onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                        data-testid="attachment-url" />
                    <Button size="sm" className="h-8 px-2.5" onClick={add} data-testid="attachment-add">
                        <Plus className="w-3.5 h-3.5" />
                    </Button>
                </div>
            )}
        </div>
    );
}


// ───────── Comments ─────────

function Comments({ card, onChange, currentUser, canEdit }) {
    const [body, setBody] = useState('');
    const items = card.comments || [];

    const add = async () => {
        const t = body.trim();
        if (!t) return;
        try {
            const r = await api.post(`/backlog/cards/${card.id}/comments`, { body: t });
            onChange([...items, r.data.comment]);
            setBody('');
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed');
        }
    };
    const remove = async (c) => {
        const old = items;
        onChange(items.filter(x => x.id !== c.id));
        try { await api.delete(`/backlog/cards/${card.id}/comments/${c.id}`); }
        catch (e) { toast.error('Failed'); onChange(old); }
    };

    return (
        <div className="space-y-3">
            {canEdit && (
                <div className="flex gap-2">
                    <Avatar name={currentUser?.name || currentUser?.email} />
                    <div className="flex-1">
                        <Textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Write a comment…"
                            rows={2}
                            className="text-sm resize-y"
                            data-testid="comment-input"
                        />
                        <div className="mt-1.5">
                            <Button size="sm" className="h-7 px-3" onClick={add} disabled={!body.trim()} data-testid="comment-submit">
                                Comment
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <div className="space-y-2">
                {items.length === 0 && !canEdit && <div className="text-xs text-slate-400 italic">No comments yet</div>}
                {[...items].reverse().map(c => (
                    <div key={c.id} className="flex gap-2 group">
                        <Avatar name={c.author_name} />
                        <div className="flex-1 min-w-0">
                            <div className="bg-white border border-slate-200 rounded p-2">
                                <div className="text-[12.5px] whitespace-pre-wrap text-slate-800">{c.body}</div>
                            </div>
                            <div className="text-[10.5px] text-slate-400 mt-0.5 flex items-center gap-2">
                                <span className="font-medium text-slate-600">{c.author_name}</span>
                                <span>·</span>
                                <span>{fmtTs(c.created_at)}</span>
                                {(c.author_id === currentUser?.id || currentUser?.is_super_admin) && (
                                    <button onClick={() => remove(c)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 ml-auto">
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


// ───────── Activity Log ─────────

function ActivityLog({ activity }) {
    if (!activity || activity.length === 0) {
        return <div className="text-xs text-slate-400 italic">No activity yet</div>;
    }
    return (
        <div className="space-y-2.5">
            {[...activity].reverse().slice(0, 50).map((a, i) => (
                <div key={i} className="text-[12px] text-slate-600">
                    <span className="font-medium text-slate-800">{a.author_name}</span>{' '}
                    <span className="text-slate-500">{describeAction(a)}</span>
                    <div className="text-[10.5px] text-slate-400">{fmtTs(a.ts)}</div>
                </div>
            ))}
        </div>
    );
}


function describeAction(a) {
    switch (a.action) {
        case 'created':    return 'created this card';
        case 'moved':      return `moved this card`;
        case 'commented':  return 'commented';
        case 'updated':    return `updated ${(a.meta?.fields || []).join(', ') || 'the card'}`;
        default:           return a.action;
    }
}


function Avatar({ name }) {
    return (
        <div className="w-7 h-7 rounded-full bg-slate-700 text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
            {(name || '?').charAt(0).toUpperCase()}
        </div>
    );
}


function fmtTs(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const now = new Date();
        const diff = (now - d) / 1000;
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}
